<template>
  <Dialog
    v-model:visible="isVisible"
    dismissable-mask
    :draggable="false"
    header="Duplicate Item"
    :modal="true"
    :style="{ width: '30vw' }"
    @hide="onCancel"
  >
    <div>
      <label for="duplicateItemName" style="font-size: 12px">Enter a new name</label>
      <InputText
        id="duplicateItemName"
        v-model="duplicateItemName"
        :class="[$style.inputText, error ? 'p-invalid' : '']"
      />
      <small v-if="error" :class="$style.errorText">
        {{ error }}
      </small>
    </div>
    <template #footer>
      <Button label="Cancel" severity="primary" outlined @click="onCancel" />
      <Button label="Duplicate" severity="primary" @click="onConfirm" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

import isAlphaNumeric from "@/utils/isAlphaNumeric";
import { useItems } from "../hooks/useItems";

const props = defineProps({
  isVisible: Boolean,
  selectedItem: Object,
});

const emit = defineEmits(["update:isVisible", "cancel"]);

const route = useRoute();
const gameId = ref(route.params.gameId as string);
const versionId = ref(route.params.versionId as string);

const duplicateItemName = ref("");
const error = ref("");

const { duplicateItem, refetchItems, itemsToDisplay } = useItems(gameId.value, versionId.value);

const isVisible = computed({
  get: () => props.isVisible,
  set: () => {
    emit("cancel");
  },
});

const onCancel = () => {
  duplicateItemName.value = "";
  error.value = "";
  emit("cancel");
};

const onConfirm = async () => {
  const trimmedName = duplicateItemName.value.trim();

  if (!trimmedName) {
    error.value = "Name cannot be empty.";
    return;
  }

  if (itemsToDisplay.value.some((item) => item?.name === trimmedName)) {
    error.value = "Item with this name already exists.";
    return;
  }

  if (!isAlphaNumeric({ str: trimmedName })) {
    error.value = "Name should be alphanumeric with no whitespaces and start with a letter.";
    return;
  }

  await duplicateItem(props.selectedItem?.id, trimmedName);
  await refetchItems();
  onCancel();
};
</script>

<style module lang="scss">
.errorText {
  display: block;
  color: #e24c4c;
}

.inputText {
  margin: 0.25rem 0;
  width: 100%;
}
</style>
